import { api } from "@/shared/api";
import { triggerDownload } from "@/shared/lib/download";
import type { ReporterBorrow } from "@/entities/explosive/model/reporterBorrow";

// FSD features/reporter — 官媒舱(AI记者)写作出稿(/ai-jizhe)。
// 契约锚点:gscq-api ReporterController(base /api/v1/tenant/reporter);
// 发起任务=异步(返 RUNNING 回执含 taskId),前端轮询 GET /drafts/{id} 聚合详情到终态。
// json 列(materialJson/planJson/sectionsJson…)后端原文透传,解析在 model/parse.ts,不在这层。

/** 体裁六种(后端 ReporterDraftService.GENRES 白名单,新增/改名以后端为准)。 */
export type ReporterGenre =
  | "COMPANY_NEWS"
  | "PRODUCT_COPY"
  | "XIAOHONGSHU"
  | "EVENT_RELEASE"
  | "BRAND_PROFILE"
  | "QA_ARTICLE";

/** 语气四挡+缺省(后端 TONES 白名单;缺省 PROFESSIONAL)。 */
export type ReporterTone = "FORMAL" | "PROFESSIONAL" | "FRIENDLY" | "MARKETING" | "LIVELY";

/** 草稿状态机:DRAFT→GENERATING→GENERATED→EDITED;失败 FAILED 可重试(后端 CAS 单口径)。 */
export type ReporterDraftStatus = "DRAFT" | "GENERATING" | "GENERATED" | "EDITED" | "FAILED";

/** 任务六种:整理/规划/拓词不动草稿状态机可反复跑;生成/重写走状态机。 */
export type ReporterTaskType =
  | "MATERIAL_EXTRACT"
  | "PLAN"
  | "GENERATE"
  | "REWRITE_FULL"
  | "REWRITE_SECTION"
  | "KEYWORD_EXPAND";

export interface ReporterTaskDto {
  id: number;
  draftId: number;
  type: ReporterTaskType;
  status: string;
  progress: number | null;
  errorMessage: string | null;
  createdAt: string;
}

export interface ReporterDraftSummaryDto {
  id: number;
  title: string | null;
  genre: ReporterGenre;
  subType: string | null;
  status: ReporterDraftStatus;
  targetWords: number | null;
  /** 批三:owner 归属(「只看我的」/成员筛的判定基础)。 */
  creatorUserId: number | null;
  /** N-11:创建人姓名(查不到 null,前端退「#id」)与 cover 槽封面缩略图(无图 null)。 */
  creatorUserName: string | null;
  coverThumbUrl: string | null;
  updatedAt: string;
}

export interface ReporterArticleDto {
  id: number;
  draftId: number;
  titlesJson: string | null;
  selectedTitle: string | null;
  summary: string | null;
  sectionsJson: string | null;
  tagsJson: string | null;
  imageSuggestionsJson: string | null;
  warningsJson: string | null;
  currentVersion: number;
}

export interface ReporterQualityDto {
  versionNo: number;
  dimScoresJson: string | null;
  totalScore: number | null;
  gate: string | null;
  grade: string | null;
  failedRulesJson: string | null;
  suggestionsJson: string | null;
}

export interface ReporterVersionDto {
  versionNo: number;
  sourceType: string;
  createdAt: string;
}

/** 草稿聚合详情(输入面+整理/规划快照+文章+质量+最近任务;轮询主口)。字段名就叫 `id`,不造 draftId 二名。 */
export interface ReporterDraftDetailDto {
  id: number;
  title: string | null;
  genre: ReporterGenre;
  subType: string | null;
  tone: ReporterTone;
  targetWords: number | null;
  materialRaw: string | null;
  materialJson: string | null;
  planJson: string | null;
  /** GEO 拓展关键词 [{word,group,source,selected}] 原文透传(解析在 model/keywords.ts)。 */
  keywordsJson: string | null;
  useKnowledge: boolean | null;
  productCode: string | null;
  sourceDraftId: number | null;
  /** 2026-10-10 C3:怎么借这条爆款(null/缺省=无;老后端无此字段)。 */
  borrow?: ReporterBorrow | null;
  status: ReporterDraftStatus;
  errorMessage: string | null;
  updatedAt: string;
  article: ReporterArticleDto | null;
  quality: ReporterQualityDto | null;
  recentTasks: ReporterTaskDto[];
}

export interface CreateDraftRequest {
  genre: ReporterGenre;
  subType?: string | null;
  tone?: ReporterTone;
  targetWords?: number | null;
  materialRaw?: string | null;
  productCode?: string | null;
  keywordsJson?: string | null;
  useKnowledge?: boolean | null;
  /** 批三「转渠道稿」溯源:由哪篇稿转出。 */
  sourceDraftId?: number | null;
  /** 0813:写法来自哪条爆款蓝图 —— 只用于后端取用登记(used_dests.reporter+1),不落草稿行。 */
  explosiveBlueprintId?: number | null;
  /**
   * 2026-10-10 C3「怎么借这条爆款」:参考片原话/写法走这个独立字段,**不拼进 materialRaw**
   * (素材会被 ai fact_guard 当证据;quotes/styleLines 不算证据)。缺省 = ai 行为逐字节不变。
   */
  borrow?: ReporterBorrow | null;
}

export interface UpdateDraftRequest {
  tone?: ReporterTone;
  targetWords?: number | null;
  materialRaw?: string | null;
  keywordsJson?: string | null;
  useKnowledge?: boolean | null;
  /** 2026-10-10 C3:同 CreateDraftRequest.borrow。 */
  borrow?: ReporterBorrow | null;
}

export interface StartTaskRequest {
  type: ReporterTaskType;
  rewriteInstruction?: string | null;
  sectionId?: string | null;
  sectionContent?: string | null;
  /** S2 摘要重写:REWRITE_SECTION 的非段落目标,白名单仅 "summary"(与 sectionId 互斥)。 */
  target?: "summary" | null;
}

/** 批二 P4 手改整包(与后端 ManualSaveRequest 同形):sectionsJson 必填,其余 null=不动。 */
export interface ManualSaveRequest {
  selectedTitle?: string | null;
  summary?: string | null;
  sectionsJson: string;
  tagsJson?: string | null;
  imageSuggestionsJson?: string | null;
}

/** 导出预检:blocking 拦死 / confirmation 需显式确认(绕过落审计)/ warning 只提示。 */
export interface ExportPreflight {
  blocking: string[];
  warning: string[];
  confirmation: string[];
}

/** 舱级元信息(创建页合规提示条数据源):租户行业与 GEO 政策(allowed|restricted|forbidden)。 */
export interface ReporterMeta {
  industryCode: string | null;
  industryName: string | null;
  geoPolicy: string | null;
}

export const reporterService = {
  getMeta: () => api.get<ReporterMeta>("/tenant/reporter/meta"),

  createDraft: (req: CreateDraftRequest) =>
    api.post<ReporterDraftDetailDto>("/tenant/reporter/drafts", req),

  updateDraft: (id: number, req: UpdateDraftRequest) =>
    api.post<ReporterDraftDetailDto>(`/tenant/reporter/drafts/${id}`, req),

  startTask: (id: number, req: StartTaskRequest) =>
    api.post<ReporterTaskDto>(`/tenant/reporter/drafts/${id}/tasks`, req),

  /** 舱内轻列表(倒序;mine=true 只看我的,服务端 creator 过滤;works 线在此之上客户端筛)。 */
  listDrafts: (limit = 20, mine = false) =>
    api.get<ReporterDraftSummaryDto[]>(`/tenant/reporter/drafts?limit=${limit}&mine=${mine}`),

  /** 批三:删除草稿(软删;生成中后端拒删)。 */
  deleteDraft: (id: number) => api.delete<void>(`/tenant/reporter/drafts/${id}`),

  getDraft: (id: number) => api.get<ReporterDraftDetailDto>(`/tenant/reporter/drafts/${id}`),

  listVersions: (articleId: number, limit = 20) =>
    api.get<ReporterVersionDto[]>(`/tenant/reporter/articles/${articleId}/versions?limit=${limit}`),

  /** 标题三选:只能在 AI 候选(titlesJson)内选;自由改标题走 manualSave(追加候选)。 */
  selectTitle: (articleId: number, title: string) =>
    api.post<ReporterArticleDto>(`/tenant/reporter/articles/${articleId}/select-title`, { title }),

  /** 批二 P4:手改文案整包保存(MANUAL_SAVE 版本+EDITED 态;不重跑评分,评分对应 AI 版本)。
   * sectionsJson 必填;其余 null=不动。 */
  manualSave: (articleId: number, req: ManualSaveRequest) =>
    api.post<ReporterArticleDto>(`/tenant/reporter/articles/${articleId}/manual-save`, req),

  /** 批二 P3:图位上传自己的图(复用海报导入通道+归属列;上传不扣积分)。 */
  uploadSlotImage: (draftId: number, slot: string, file: File) => {
    const form = new FormData();
    form.append("file", file);
    return api.post<{ taskId: number; assetId: number; thumbUrl: string | null }>(
      `/tenant/reporter/drafts/${draftId}/images/${slot}/upload`,
      form
    );
  },

  /** 批二 P3:移除图位(解除归属不删作品;幂等)。 */
  removeSlotImage: (draftId: number, slot: string) =>
    api.post<{ detached: number }>(
      `/tenant/reporter/drafts/${draftId}/images/${slot}/remove`,
      null
    ),

  rollback: (articleId: number, versionNo: number) =>
    api.post<ReporterArticleDto>(
      `/tenant/reporter/articles/${articleId}/rollback/${versionNo}`,
      null
    ),

  exportPreflight: (articleId: number) =>
    api.post<ExportPreflight>(`/tenant/reporter/articles/${articleId}/export-preflight`, null),

  /** 导出(md|docx)。confirmation 未确认 → 后端 409;文件名以响应头为准,这里只兜底。 */
  exportArticle: async (articleId: number, format: "md" | "docx", confirm: boolean) => {
    const res = await api.sendBlob(
      "POST",
      `/tenant/reporter/articles/${articleId}/export?format=${format}&confirm=${confirm}`
    );
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const fallback = `文章.${format === "docx" ? "docx" : "md"}`;
    try {
      triggerDownload(
        url,
        filenameFromDisposition(res.headers.get("Content-Disposition")) ?? fallback
      );
    } finally {
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    }
  },
};

/** 解 `attachment; filename*=UTF-8''…`(后端 URLEncoder 编码;+ 是空格)。 */
function filenameFromDisposition(header: string | null): string | null {
  if (!header) return null;
  const m = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (!m) return null;
  try {
    return decodeURIComponent(m[1].replace(/\+/g, "%20"));
  } catch {
    return null;
  }
}
